import { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, BookOpen, ShoppingBag, ArrowRight, Check, Sparkles } from "lucide-react";
import EbookModal from "@/components/EbookModal";
import CheckoutModal from "@/components/CheckoutModal";

const HeroProductCard = ({ icon: Icon, tier, name, description, priceLabel, features, cta, ctaStyle, onAction }) => (
  <div className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2"
    style={{ background: "white", border: "1px solid var(--ll-border)", boxShadow: "0 2px 16px rgba(42,54,35,0.04)" }}>
    <div className="p-7 md:p-8 flex flex-col flex-1">
      <div className="flex items-center justify-between mb-5">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)" }}>
          <Icon size={22} style={{ color: "var(--ll-green)" }} />
        </div>
        <span className="text-[9px] tracking-wider uppercase px-3 py-1 rounded-full"
          style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{tier}</span>
      </div>
      <h3 className="font-serif text-2xl sm:text-3xl font-medium mb-3" style={{ color: "var(--ll-text)" }}>{name}</h3>
      <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{description}</p>
      <ul className="space-y-2 mb-6">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
            <Check size={14} style={{ color: "var(--ll-green)" }} /> {f}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between mt-auto pt-4" style={{ borderTop: "1px solid var(--ll-border)" }}>
        <span className="font-serif text-2xl font-semibold" style={{ color: "var(--ll-text)" }}>{priceLabel}</span>
        <button data-testid={`hero-cta-${name.toLowerCase().replace(/\s/g, "-")}`} onClick={onAction}
          className="rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center gap-1.5"
          style={{ ...ctaStyle, fontFamily: "'Outfit', sans-serif" }}>
          {cta} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  </div>
);

const CTA_STYLE = { background: "var(--ll-cta)", color: "var(--ll-cta-text)" };
const CTA_STYLE_GREEN = { background: "var(--ll-green)", color: "white" };
const COURSE_FEATURES = ["10 video lessons", "Interactive quizzes", "EQ exercises", "Journal export"];
const WORKBOOK_FEATURES = ["160+ pages physical workbook", "Printed & shipped to you", "The Lemon Framework\u2122", "Complete wellness system"];
const COLLECTION_FEATURES = ["Free resources available", "Journals from R130", "Planners & trackers", "Premium workbooks"];

const CTA_STYLE_WAITLIST = { background: "var(--ll-accent)", color: "var(--ll-text)" };

const HeroProducts = ({ products }) => {
  const [ebookOpen, setEbookOpen] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState(null);

  const course = products.find((p) => p.id === "course-burnout-to-balance-in-action");
  const workbook = products.find((p) => p.id === "bundle-workbook-physical");

  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            Our Signature Products
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light" style={{ color: "var(--ll-text)" }}>
            From Burnout to{" "}
            <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>Wellness</em>
          </h2>
          <p className="text-base mt-4 max-w-2xl mx-auto" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
            Evidence-informed products to help women professionals move from burnout and overwhelm to sustainable wellness.
          </p>
        </div>

        {/* 3 Hero Products */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {/* Course */}
          <HeroProductCard
            icon={GraduationCap} tier="Course" name="Burnout Recovery Course"
            description="10 video lessons with interactive quizzes and emotional intelligence exercises. Your complete burnout recovery programme."
            priceLabel="R2,499" features={COURSE_FEATURES}
            cta="Get Course" ctaStyle={CTA_STYLE}
            onAction={() => course && setCheckoutProduct({ ...course, price: course.price_zar })}
          />

          {/* Workbook — Physical Edition (Now Available) */}
          <HeroProductCard
            icon={BookOpen} tier="Physical" name="The Lemonade Living Wellness Workbook"
            description="Our signature 160+ page spiral-bound wellness workbook for Creatives, Visionaries & Multi-Passionate Women. Free shipping, delivered in ~2 weeks."
            priceLabel="R799"
            features={WORKBOOK_FEATURES}
            cta="Order Now" ctaStyle={CTA_STYLE}
            onAction={() => window.location.href = "/shop"}
          />

          {/* Digital Collection → Shop */}
          <HeroProductCard
            icon={ShoppingBag} tier="Collection" name="Digital Wellness Collection"
            description="Journals, planners, trackers, and workbooks — from free resources to premium transformation tools. Start with our free downloads."
            priceLabel="From R0" features={COLLECTION_FEATURES}
            cta="Browse Shop" ctaStyle={CTA_STYLE_GREEN}
            onAction={() => window.location.href = "/shop"}
          />
        </div>

        {/* Free ebook banner */}
        <div className="rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-5"
          style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.12), rgba(163,184,153,0.15))", border: "1px solid var(--ll-border)" }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--ll-accent)" }}>
              <Sparkles size={20} style={{ color: "var(--ll-text)" }} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Start Free — Download Burnout To Balance</p>
              <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Our comprehensive 8-chapter ebook. No cost, no commitment.</p>
            </div>
          </div>
          <button data-testid="hero-free-ebook" onClick={() => setEbookOpen(true)}
            className="rounded-full px-6 py-3 text-sm font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5 flex-shrink-0"
            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
            Download Free <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <EbookModal open={ebookOpen} onClose={() => setEbookOpen(false)} />
      {checkoutProduct && (
        <CheckoutModal product={checkoutProduct} open={!!checkoutProduct} onClose={() => setCheckoutProduct(null)} />
      )}
    </section>
  );
};

export default HeroProducts;
